import { apiService } from './api';
import { getApiConfig } from '../config/api';

const apiConfig = getApiConfig();

// ── Types (mirror C# DTOs) ─────────────────────────────────────────────────

export interface TicketDto {
  ticketId: number;
  ticketSubject: string;
  ticketPriority: string;
  ticketStatus: string;
  ticketDescription: string | null;
  ticketCreatedAt: string;
  userId: number;
}

export interface CreateTicketRequestDto {
  ticketSubject: string;
  ticketPriority: string;
  ticketStatus?: string; // defaults to "Open"
  ticketDescription?: string;
}

export interface UpdateTicketRequestDto {
  ticketSubject: string;
  ticketPriority: string;
  ticketStatus: string;
}

// ── API Service ────────────────────────────────────────────────────────────

export const ticketApi = {
  // GET /api/Ticket  — all tickets (admin)
  getAllTickets: async (): Promise<TicketDto[]> => {
    const response = await apiService.request<TicketDto[]>(apiConfig.ENDPOINTS.GET_ALL_TICKETS);
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch tickets');
    }
    return response.data;
  },

  // GET /api/Ticket/userTickets  — tickets for the current user
  getUserTickets: async (): Promise<TicketDto[]> => {
    const response = await apiService.request<TicketDto[]>(apiConfig.ENDPOINTS.GET_USER_TICKETS);
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch user tickets');
    }
    return response.data;
  },

  // GET /api/Ticket/{ticketId}
  getTicketById: async (ticketId: number): Promise<TicketDto> => {
    const endpoint = apiConfig.ENDPOINTS.GET_TICKET_BY_ID.replace(':ticketId', ticketId.toString());
    const response = await apiService.request<TicketDto>(endpoint);
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch ticket');
    }
    return response.data;
  },

  // POST /api/Ticket/createTicket
  createTicket: async (data: CreateTicketRequestDto): Promise<TicketDto> => {
    const response = await apiService.request<TicketDto>(apiConfig.ENDPOINTS.CREATE_TICKET, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to create ticket');
    }
    return response.data;
  },

  // PUT /api/Ticket/{ticketId}
  updateTicket: async (ticketId: number, data: UpdateTicketRequestDto): Promise<TicketDto> => {
    const endpoint = apiConfig.ENDPOINTS.UPDATE_TICKET.replace(':ticketId', ticketId.toString());
    const response = await apiService.request<TicketDto>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to update ticket');
    }
    return response.data;
  },

  // DELETE /api/Ticket/{ticketId}
  deleteTicket: async (ticketId: number): Promise<void> => {
    const endpoint = apiConfig.ENDPOINTS.DELETE_TICKET.replace(':ticketId', ticketId.toString());
    const response = await apiService.request(endpoint, {
      method: 'DELETE',
    });
    if (!response.success) {
      throw new Error(response.error || 'Failed to delete ticket');
    }
  },
};
